/* eslint-disable eqeqeq */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Col, Row, Form } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import FormInput from "../../../Components/Shared/FormInput";
import Buttons from "../../../Components/Shared/Button";
import { Validators } from "../../../Utilities/Validator";
import StartupHeader from "../../../Components/StartupHeader";
import { toast } from "react-toastify";
import {
  unsubscribeFromMailingList,
  resubscribeToMailingList,
} from "../../../Services/UnsubscribeService";

export default function Unsubscribe() {
  const history = useHistory();
  const location = useLocation();
  const toastId = "custom_toast";

  const [state, setState] = useState({
    email: "",
  });

  const [emailError, setEmailError] = useState(false);
  const [emailValidError, setEmailValidError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // The address we actually unsubscribed, kept so the re-subscribe button can
  // undo it without asking them to type it again.
  const [unsubscribedEmail, setUnsubscribedEmail] = useState("");
  const [isResubscribing, setIsResubscribing] = useState(false);
  const [isResubscribed, setIsResubscribed] = useState(false);

  // Marketing emails link here with ?email=<address>, so a recipient who
  // clicks Unsubscribe is unsubscribed on arrival rather than being asked to
  // retype an address we already know. Anyone arriving without it still gets
  // the manual form.
  const emailFromLink = new URLSearchParams(location.search).get("email") || "";
  const [isAutoUnsubscribing, setIsAutoUnsubscribing] = useState(
    !!emailFromLink.trim()
  );

  useEffect(() => {
    document.body.classList.add("home-page");

    return function cleanup() {
      document.body.classList.remove("home-page");
    };
  }, []);

  const submitUnsubscribe = useCallback(async (email) => {
    const response = await unsubscribeFromMailingList({ email: email.trim() });
    return (
      response.success || response.message === "Email unsubscribed successfully"
    );
  }, []);

  // Runs once per page view. Any failure drops them onto the manual form with
  // the address prefilled, so the click is never a dead end.
  const autoUnsubscribeAttempted = useRef(false);
  useEffect(() => {
    const email = emailFromLink.trim();
    if (!email) return;
    if (autoUnsubscribeAttempted.current) return;
    autoUnsubscribeAttempted.current = true;

    if (Validators.isEmail(email, "email").error) {
      setState({ email });
      setEmailValidError(true);
      setIsAutoUnsubscribing(false);
      return;
    }

    submitUnsubscribe(email)
      .then((ok) => {
        if (ok) {
          setUnsubscribedEmail(email);
          setIsSuccess(true);
        } else {
          setState({ email });
          toast.error("Failed to unsubscribe. Please try again below.", {
            toastId: toastId,
          });
        }
      })
      .catch((error) => {
        console.error("Unsubscribe error:", error);
        setState({ email });
        toast.error(
          "An error occurred while processing your request. Please try again below.",
          { toastId: toastId }
        );
      })
      .finally(() => setIsAutoUnsubscribing(false));
  }, [emailFromLink, submitUnsubscribe]);

  const handleInputChange = (value) => {
    setState((prevProps) => ({
      ...prevProps,
      email: value,
    }));

    if (value === "" || value === undefined) {
      setEmailError(true);
      setEmailValidError(false);
    } else {
      setEmailError(false);
      // Validate email format
      const emailValidation = Validators.isEmail(value, "email");
      if (emailValidation.error) {
        setEmailValidError(true);
      } else {
        setEmailValidError(false);
      }
    }
  };

  const validateForm = () => {
    let isValid = true;

    // Check if email is empty
    if (state.email === "" || state.email === undefined) {
      setEmailError(true);
      isValid = false;
    } else {
      setEmailError(false);

      // Check if email format is valid
      const emailValidation = Validators.isEmail(state.email, "email");
      if (emailValidation.error) {
        setEmailValidError(true);
        isValid = false;
      } else {
        setEmailValidError(false);
      }
    }

    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const email = state.email.trim();
      const ok = await submitUnsubscribe(email);

      if (ok) {
        setUnsubscribedEmail(email);
        setIsSuccess(true);
        toast.success(
          "You have been successfully unsubscribed from our mailing list.",
          { toastId: toastId }
        );
      } else {
        toast.error("Failed to unsubscribe. Please try again later.", {
          toastId: toastId,
        });
      }
    } catch (error) {
      console.error("Unsubscribe error:", error);
      toast.error(
        "An error occurred while processing your request. Please try again later.",
        { toastId: toastId }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResubscribe = async () => {
    if (!unsubscribedEmail) return;

    setIsResubscribing(true);

    try {
      const response = await resubscribeToMailingList({
        email: unsubscribedEmail,
      });

      if (
        response.success ||
        response.message === "Email re-subscribed successfully"
      ) {
        setIsResubscribed(true);
        toast.success("You have been added back to our mailing list.", {
          toastId: toastId,
        });
      } else {
        toast.error("Failed to re-subscribe. Please try again later.", {
          toastId: toastId,
        });
      }
    } catch (error) {
      console.error("Re-subscribe error:", error);
      toast.error(
        "An error occurred while processing your request. Please try again later.",
        { toastId: toastId }
      );
    } finally {
      setIsResubscribing(false);
    }
  };

  const navigateToHome = () => {
    history.push(`${process.env.PUBLIC_URL}/home`);
  };

  const renderBody = () => {
    if (isAutoUnsubscribing) {
      return (
        <div className="unsubscribe-processing text-center">
          <h2 className="mb-3">Unsubscribing...</h2>
          <p className="text-muted mb-0">
            One moment while we remove you from our mailing list.
          </p>
        </div>
      );
    }

    if (isResubscribed) {
      return (
        <div className="unsubscribe-success text-center">
          <div className="mb-4">
            <i
              className="fa fa-check-circle text-success"
              style={{ fontSize: "4rem" }}
            ></i>
          </div>
          <h2 className="text-success mb-3">You're Subscribed Again</h2>
          <p className="text-muted mb-4">
            Welcome back. You will continue to receive emails from us.
          </p>
          <Buttons
            variant="primary"
            title="Return to Home"
            type="button"
            size="md"
            onClick={navigateToHome}
            className="px-4"
          />
        </div>
      );
    }

    if (isSuccess) {
      return (
        <div className="unsubscribe-success text-center">
          <div className="mb-4">
            <i
              className="fa fa-check-circle text-success"
              style={{ fontSize: "4rem" }}
            ></i>
          </div>
          <h2 className="text-success mb-3">Successfully Unsubscribed</h2>
          <p className="text-muted mb-4">
            {unsubscribedEmail ? `${unsubscribedEmail} has ` : "You have "}
            been removed from our mailing list. You will no longer receive
            marketing emails from us.
          </p>
          <Buttons
            variant="primary"
            title="Return to Home"
            type="button"
            size="md"
            onClick={navigateToHome}
            className="px-4"
          />
          {/* Unsubscribing happens on arrival now, so this is the way back for
              anyone who did not mean to -- including someone whose link was
              followed by a mail scanner rather than by them. */}
          {unsubscribedEmail && (
            <div className="text-center mt-4">
              <small className="text-muted d-block mb-2">
                Didn't mean to unsubscribe?
              </small>
              <Buttons
                variant="link"
                title={
                  isResubscribing ? "Re-subscribing..." : "Re-subscribe me"
                }
                type="button"
                size="md"
                onClick={handleResubscribe}
                disabled={isResubscribing}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="unsubscribe-form">
        <h1 className="text-center mb-4">Unsubscribe from Mailing List</h1>
        <p className="text-center text-muted mb-4">
          We're sorry to see you go. Enter your email address below to
          unsubscribe from our mailing list.
        </p>

        <Form onSubmit={handleSubmit} noValidate>
          <Row className="justify-content-center">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address *</Form.Label>
                <FormInput
                  value={state.email}
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  className="form-control"
                  onChange={handleInputChange}
                  required
                />
                {emailError && (
                  <div className="text-danger mt-2">
                    Email address is required
                  </div>
                )}
                {emailValidError && (
                  <div className="text-danger mt-2">
                    Please enter a valid email address
                  </div>
                )}
              </Form.Group>

              <div className="text-center">
                <Buttons
                  variant="danger"
                  title={isSubmitting ? "Unsubscribing..." : "Unsubscribe"}
                  type="submit"
                  size="md"
                  disabled={isSubmitting}
                  className="px-4"
                />
              </div>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Changed your mind?{" "}
                  <span
                    className="text-primary cursor-pointer"
                    onClick={navigateToHome}
                    style={{ textDecoration: "underline" }}
                  >
                    Go back to home
                  </span>
                </small>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  return (
    <React.Fragment>
      <StartupHeader />

      <div className="main-content">
        <div className="main-banner">
          <div className="dashboard-wrapper resumes-page">
            <div className="container">
              <div className="dashboard-box dashboard-col-bg public-wrapper outline-form">
                <div className="text-right">
                  <span
                    className="icon icon-close ml-auto black cursor-pointer"
                    onClick={navigateToHome}
                  ></span>
                </div>

                {renderBody()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
